import type { ReactNode } from 'react';
import { X, CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useToastStore } from '@/stores/toastStore';
import type { ToastKind } from '@/stores/toastStore';

const KIND_STYLE: Record<ToastKind, { bg: string; border: string; icon: ReactNode }> = {
  info:    { bg: '#eff6ff', border: '#3b82f6', icon: <Info size={16} color="#1d4ed8" /> },
  success: { bg: '#ecfdf5', border: '#10b981', icon: <CheckCircle2 size={16} color="#047857" /> },
  warning: { bg: '#fffbeb', border: '#f59e0b', icon: <AlertTriangle size={16} color="#b45309" /> },
  error:   { bg: '#fef2f2', border: '#ef4444', icon: <AlertCircle size={16} color="#b91c1c" /> },
};

/**
 * Container global des toasts (ticket SIMGROUP-2026-002 M-01).
 * Monté une seule fois dans AppLayout. Position fixe : top-right desktop,
 * top-center mobile. Empile les toasts, chaque toast fermable manuellement.
 */
export const ToastHost = () => {
  const { toasts, dismiss } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position:      'fixed',
        top:           16,
        right:         16,
        zIndex:        2000,
        display:       'flex',
        flexDirection: 'column',
        gap:           8,
        maxWidth:      420,
        width:         'calc(100vw - 32px)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => {
        const style = KIND_STYLE[t.kind];
        return (
          <div
            key={t.id}
            role={t.kind === 'error' || t.kind === 'warning' ? 'alert' : 'status'}
            style={{
              background:   style.bg,
              border:       `1px solid ${style.border}`,
              borderLeft:   `4px solid ${style.border}`,
              borderRadius: 6,
              padding:      '10px 12px',
              display:      'flex',
              alignItems:   'flex-start',
              gap:          10,
              boxShadow:    '0 4px 12px rgba(0,0,0,0.12)',
              pointerEvents: 'auto',
              fontSize:     13,
            }}
          >
            <div style={{ flexShrink: 0, paddingTop: 1 }}>{style.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#111827', marginBottom: t.message ? 3 : 0 }}>
                {t.title}
              </div>
              {t.message && (
                <div style={{ color: '#374151', fontSize: 12, lineHeight: 1.4, wordBreak: 'break-word' }}>
                  {t.message}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Fermer"
              style={{
                flexShrink: 0,
                background: 'transparent',
                border:     'none',
                cursor:     'pointer',
                color:      '#6b7280',
                padding:    2,
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
};
